import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getOrgEvents, getOrgFeedbacks, getEventFeedbacks, createFeedback } from '../../api';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/Modal';
import Alert from '../../components/Alert';
import Loading from '../../components/Loading';
import EmptyState from '../../components/EmptyState';
import { StarRating, StarInput } from '../../components/StarRating';
import {
  ChatBubbleLeftEllipsisIcon,
  CalendarIcon,
  PlusIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';

export default function FeedbackPage() {
  const { orgId } = useParams();
  const { user, organizations } = useAuth();
  const [events, setEvents] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(null); // eventId
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [feedbackForm, setFeedbackForm] = useState({ content: '', anonymous: false, rating: 5 });
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [fbPage, setFbPage] = useState(1);
  const FB_PAGE_SIZE = 5;

  const org = organizations.find(o => o._id === orgId);

  useEffect(() => { loadData(); }, [orgId]);

  const loadData = async () => {
    try {
      const [evRes, fbRes] = await Promise.all([
        getOrgEvents(orgId),
        getOrgFeedbacks(orgId)
      ]);
      setEvents(evRes.data);
      setFeedbacks(fbRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

  // Past events where user attended and can leave feedback
  const eligibleEvents = events.filter(e => {
    const isPast = e.date < todayStr;
    const attended = e.attending?.some(a => a.userId === user._id);
    const alreadyFeedbacked = feedbacks.some(f => f.eventId === e._id && f.userId === user._id);
    return isPast && attended && !alreadyFeedbacked;
  }).sort((a, b) => new Date(b.date) - new Date(a.date));

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await createFeedback({
        eventId: showCreate,
        ...feedbackForm
      });
      await loadData();
      setShowCreate(null);
      setFeedbackForm({ content: '', anonymous: false, rating: 5 });
      setSuccess('Feedback submitted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit feedback');
    }
  };

  const toggleExpand = (eventId) => {
    setExpandedEvents(prev => {
      const next = new Set(prev);
      next.has(eventId) ? next.delete(eventId) : next.add(eventId);
      return next;
    });
  };

  const getEventById = (eventId) => events.find(e => e._id === eventId);

  // Group feedbacks by event, sort by event date desc
  const feedbacksByEvent = {};
  feedbacks.forEach(fb => {
    if (!feedbacksByEvent[fb.eventId]) feedbacksByEvent[fb.eventId] = [];
    feedbacksByEvent[fb.eventId].push(fb);
  });

  const allFbEntries = Object.entries(feedbacksByEvent)
    .filter(([eventId]) => getEventById(eventId))
    .sort(([aId], [bId]) => {
      const aDate = getEventById(aId)?.date || '';
      const bDate = getEventById(bId)?.date || '';
      return bDate.localeCompare(aDate);
    });
  const totalFbPages = Math.ceil(allFbEntries.length / FB_PAGE_SIZE);
  const pagedFbEntries = allFbEntries.slice((fbPage - 1) * FB_PAGE_SIZE, fbPage * FB_PAGE_SIZE);

  if (loading) return <Loading />;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Feedback</h2>
          <div className="subtitle">{org?.name || 'Organization'}</div>
        </div>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Leave Feedback section */}
      {eligibleEvents.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <PlusIcon className="icon" /> Leave Feedback
          </h3>
          <div className="events-list">
            {eligibleEvents.map(event => (
              <div key={event._id} className="event-row" onClick={() => setShowCreate(event._id)}>
                <div className="event-row-date">
                  <div className="date-day">{new Date(event.date + 'T12:00:00').getDate()}</div>
                  <div className="date-month">{new Date(event.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short' })}</div>
                </div>
                <div className="event-row-info">
                  <div className="event-row-title">{event.title}</div>
                  <div className="event-row-meta">
                    {event.location && <span>{event.location}</span>}
                  </div>
                </div>
                <span className="badge badge-warning"><StarIcon style={{ width: 14, height: 14 }} /> Leave feedback</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Feedbacks */}
      <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <ChatBubbleLeftEllipsisIcon className="icon" /> All Feedbacks
      </h3>

      {feedbacks.length === 0 ? (
        <EmptyState
          icon={<ChatBubbleLeftEllipsisIcon style={{ width: 48, height: 48 }} />}
          title="No Feedbacks Yet"
          description="Feedbacks will appear here after events have taken place."
        />
      ) : (
        <>
          {pagedFbEntries.map(([eventId, eventFeedbacks]) => {
            const event = getEventById(eventId);
            if (!event) return null;
            const avgRating = eventFeedbacks.reduce((sum, f) => sum + f.rating, 0) / eventFeedbacks.length;
            const isExpanded = expandedEvents.has(eventId);

            return (
              <div key={eventId} style={{ marginBottom: 12 }}>
                <div
                  onClick={() => toggleExpand(eventId)}
                  style={{
                    padding: '12px 16px',
                    background: 'var(--bg-card)',
                    borderRadius: isExpanded ? 'var(--radius) var(--radius) 0 0' : 'var(--radius)',
                    border: '1px solid var(--border)',
                    borderBottom: isExpanded ? 'none' : '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>{event.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <CalendarIcon className="icon" /> {new Date(event.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{eventFeedbacks.length} feedback{eventFeedbacks.length !== 1 ? 's' : ''}</div>
                      <StarRating rating={Math.round(avgRating)} size={14} />
                    </div>
                    <ChevronDownIcon style={{ width: 18, height: 18, color: 'var(--text-muted)', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                  </div>
                </div>
                {isExpanded && (
                  <div style={{
                    border: '1px solid var(--border)',
                    borderRadius: '0 0 var(--radius) var(--radius)',
                    overflow: 'hidden'
                  }}>
                    {eventFeedbacks.map(fb => (
                      <div key={fb._id} style={{ padding: 16, borderBottom: '1px solid var(--border)' }}>
                        <div className="feedback-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div className="member-avatar" style={{ marginRight: 12 }}>
                            {fb.avatar ? (
                              <img src={fb.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              fb.displayName?.charAt(0)?.toUpperCase()
                            )}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div className="feedback-author">{fb.displayName}</div>
                            <div className="feedback-date">{new Date(fb.createdAt).toLocaleDateString()}</div>
                          </div>
                        </div>
                        <StarRating rating={fb.rating} size={14} />
                        <div className="feedback-content">{fb.content}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {totalFbPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
              <button className="btn btn-secondary btn-sm" disabled={fbPage <= 1} onClick={() => setFbPage(p => p - 1)}>Previous</button>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Page <strong>{fbPage}</strong> / <strong>{totalFbPages}</strong></span>
              <button className="btn btn-secondary btn-sm" disabled={fbPage >= totalFbPages} onClick={() => setFbPage(p => p + 1)}>Next</button>
            </div>
          )}
        </>
      )}

      {/* Create Feedback Modal */}
      <Modal isOpen={!!showCreate} onClose={() => setShowCreate(null)} title="Leave Feedback"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowCreate(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmitFeedback}>Submit Feedback</button>
          </>
        }
      >
        <form onSubmit={handleSubmitFeedback}>
          {showCreate && (
            <div style={{ marginBottom: 16, padding: 12, background: 'var(--bg-card)', borderRadius: 'var(--radius)', fontSize: 14 }}>
              <strong>{getEventById(showCreate)?.title}</strong>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                {getEventById(showCreate)?.date && new Date(getEventById(showCreate).date + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
          )}
          <div className="form-group">
            <label>Rating</label>
            <StarInput value={feedbackForm.rating} onChange={v => setFeedbackForm({...feedbackForm, rating: v})} />
          </div>
          <div className="form-group">
            <label>Your Feedback</label>
            <textarea
              className="form-input"
              rows={4}
              value={feedbackForm.content}
              onChange={e => setFeedbackForm({...feedbackForm, content: e.target.value})}
              placeholder="Share your thoughts about this event..."
              required
            />
          </div>
          <label className="checkbox-group">
            <input
              type="checkbox"
              checked={feedbackForm.anonymous}
              onChange={e => setFeedbackForm({...feedbackForm, anonymous: e.target.checked})}
            />
            Submit anonymously
          </label>
        </form>
      </Modal>
    </div>
  );
}
